import { io } from "socket.io-client";
import { URL } from "../BaseURL";

// ─── Socket instance ──────────────────────────────────────────────────────────
export const socket = io(URL, {
  autoConnect:          false,
  transports:           ["websocket", "polling"],

  // Reconnection
  reconnection:         true,
  reconnectionAttempts: Infinity,
  reconnectionDelay:    1000,
  reconnectionDelayMax: 5000,
  randomizationFactor:  0.3,

  // Connection health
  timeout:              10000,
  pingTimeout:          20000,
  pingInterval:         10000,

  withCredentials:      true,
});

// ─── Dev logging ──────────────────────────────────────────────────────────────
if (process.env.NODE_ENV === "development") {
  socket.on("connect",           () => console.log("[socket] connected:", socket.id));
  socket.on("disconnect",        (reason) => console.warn("[socket] disconnected:", reason));
  socket.on("reconnect_attempt", (n) => console.log("[socket] reconnect attempt:", n));
  socket.on("reconnect",         (n) => console.log("[socket] reconnected after", n, "attempts"));
  socket.on("reconnect_failed",  () => console.error("[socket] reconnect failed"));
  socket.on("connect_error",     (err) => console.error("[socket] connect error:", err.message));
}

// ─────────────────────────────────────────────────────────────────────────────
// PRESENCE
// ─────────────────────────────────────────────────────────────────────────────

export const registerUser = (userId) => {
  socket.emit("register-user", { userId: String(userId) });
};

// Call this from handleLogout — emits the event then fully disconnects.
// Setting reconnection:false first prevents the client from immediately
// reconnecting after a deliberate logout.
export const logoutUser = (userId) => {
  socket.emit("user-logout", { userId: String(userId) });
  socket.io.opts.reconnection = false;  // stop auto-reconnect on this instance
  socket.disconnect();
};

// ─────────────────────────────────────────────────────────────────────────────
// CALL HELPERS
// ─────────────────────────────────────────────────────────────────────────────

export const callUser = (callerId, calleeId) => {
  socket.emit("call-user", {
    callerId: String(callerId),
    calleeId: String(calleeId),
  });
};

export const acceptCall = (callId) => {
  socket.emit("accept-call", { callId });
};

export const rejectCall = (callId) => {
  socket.emit("reject-call", { callId });
};

export const endCall = (callId) => {
  socket.emit("end-call", { callId });
};

export const sendWebrtcOffer = (callId, offer, toUserId) => {
  socket.emit("webrtc-offer", {
    callId,
    offer,
    to: String(toUserId),
  });
};

export const sendWebrtcAnswer = (callId, answer, toUserId) => {
  socket.emit("webrtc-answer", {
    callId,
    answer,
    to: String(toUserId),
  });
};

export const sendIceCandidate = (callId, candidate, toUserId) => {
  socket.emit("ice-candidate", {
    callId,
    candidate,
    to: String(toUserId),
  });
};

// ─────────────────────────────────────────────────────────────────────────────
// CHAT HELPERS
// ─────────────────────────────────────────────────────────────────────────────

export const sendMessage = (fromId, toId, text) => {
  if (!text?.trim()) return;
  socket.emit("send-message", {
    fromId: String(fromId),
    toId:   String(toId),
    text:   text.trim(),
  });
};

export const getHistory = (userId, withUserId) => {
  socket.emit("get-history", {
    userId:     String(userId),
    withUserId: String(withUserId),
  });
};

export const markRead = (userId, fromId) => {
  socket.emit("mark-read", {
    userId: String(userId),
    fromId: String(fromId),
  });
};

// ─────────────────────────────────────────────────────────────────────────────
// CHAT LISTENERS — attach once, call the provided handler
// Usage:  const off = onReceiveMessage((msg) => { ... });
//         return off;  // in useEffect cleanup
// ─────────────────────────────────────────────────────────────────────────────

export const onReceiveMessage = (handler) => {
  socket.on("receive-message", handler);
  return () => socket.off("receive-message", handler);
};

export const onMessageSent = (handler) => {
  socket.on("message-sent", handler);
  return () => socket.off("message-sent", handler);
};

export const onChatHistory = (handler) => {
  socket.on("chat-history", handler);
  return () => socket.off("chat-history", handler);
};

export const onMessagesRead = (handler) => {
  socket.on("messages-read", handler);
  return () => socket.off("messages-read", handler);
};